import { LinkButton } from "@/DesignSystem/Molecules/LinkButton";
import { Label } from "@/DesignSystem/Molecules/ToolBox/Label";
import { ErrorMessage } from "./BuildingBlocks/ErrorMessage";
import { HintMessage } from "./BuildingBlocks/HintMessage";

import styles from "./TextFieldContainer.module.scss";

type TextInputLabelPosition = "top" | "left";
type TextInputCtaPosition = "top" | "bottom" | "none";

interface ITextFieldContainerProps {
    /**
     * The optional text field container ID.
     */
    id?: string;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
   * The width of the container in pixels.
   */
    width?: string;

    /**
   * Label text: can be positioned on top, left, or omitted.
   */
    label?: string;

    /**
   * Position of the label: 'top' or 'left'.
   */
    labelPosition?: TextInputLabelPosition;

    /**
   * To indicate whether the field is required. If true, displays a red asterisk next to the label.
   */
    required?: boolean;

    /**
   * CTA link button displayed above, below, or omitted.
   */
    ctaButton?: string;

    /**
   * Position of the CTA link button: 'top', 'bottom', or 'none'.
   */
    ctaButtonPosition?: TextInputCtaPosition;

    /**
   * Callback function when the CTA link button is clicked.
   */
    onCtaClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

    /**
   * Children components to render inside the container.
   */
    children: React.ReactNode;

    /**
   * Hint text displayed below the input.
   */
    hintText?: string;

    /**
   * Error message text displayed below the input, instead of the hint.
   */
    errorMessage?: string;

    /**
   * Error message id.
   */
    errorMessageId?: string;
}

/**
 * Reusable container, that handles layout and styling for elements positioning.
 * @param {ITextFieldContainerProps} param The props used to render the text field container.
 * @returns The rendered component.
 */
function TextFieldContainer({
    id,
    dataCy,
    width,
    label,
    labelPosition = "top",
    required,
    ctaButton,
    ctaButtonPosition = "none",
    onCtaClick,
    children,
    hintText,
    errorMessage,
    errorMessageId
}: Readonly<ITextFieldContainerProps>): JSX.Element {
    const labelPositionStyle = labelPosition === "left" ? styles.labelLeft : styles.labelTop;
    const ctaButtonPositionStyle = ctaButtonPosition === "top" ? styles.ctaTop : styles.ctaBottom;

    return (
        <div
            style={{ width }}
            data-cy={dataCy}
            className={`${styles.container} 
            ${label ? (labelPositionStyle) : ""} ${ctaButtonPosition !== "none" ? (ctaButtonPositionStyle) : ""}`
            }
        >

            {(label && (
                <div className={`${styles.label} ${labelPosition === "left" ? styles.left : styles.top}`}>
                    <Label
                        label={label}
                        required={required}
                        htmlFor={id}
                    />
                </div>
            ))}

            {ctaButton && ctaButtonPosition !== "none" && (
                <div className={`${styles.ctaButton} ${ctaButtonPosition === "top" ? styles.top : styles.bottom}`}>
                    <LinkButton
                        size="sm"
                        underline={false}
                        label={ctaButton}
                        onClick={onCtaClick}
                    />
                </div>
            )}

            <div className={styles.inputWrapper}>
                {children}
            </div>

            {hintText && !errorMessage && (
                <div className={styles.infoText}>
                    <HintMessage hintText={hintText} />
                </div>
            )}

            {errorMessage && (
                <div className={styles.infoText}>
                    <ErrorMessage errorText={errorMessage} id={errorMessageId} />
                </div>
            )}

        </div>
    );
}

export { TextFieldContainer, TextInputCtaPosition, TextInputLabelPosition };
